import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

const TITLE_SUFFIX = 'XVer';

/**
 * Route-level page identity: sets `document.title` to `parts · XVer` (most specific first) while
 * mounted, and focuses the returned heading once per pathname. Attach the ref to the page `h1`
 * with `tabIndex={-1}`; pass no parts while the title is still loading. The heading is not
 * focused when focus moved after the navigation (the user tabbed on while the title loaded) or
 * when the navigation keeps the navigation drawer open (a project switch).
 */
export default function usePageHeading(...parts: (string | undefined)[]) {
    const headingRef = useRef<HTMLHeadingElement>(null);
    const focusedPath = useRef<string | null>(null);
    const focusAtNavigation = useRef<Element | null>(null);
    const { pathname, state } = useLocation();
    const keepsNavigationOpen =
        (state as { keepNavigationOpen?: boolean } | null)?.keepNavigationOpen === true;
    const title = parts.every(Boolean) && parts.length > 0 ? parts.join(' · ') : undefined;

    useEffect(() => {
        if (!title) return;
        const previous = document.title;
        document.title = `${title} · ${TITLE_SUFFIX}`;
        return () => {
            document.title = previous;
        };
    }, [title]);

    useEffect(() => {
        focusAtNavigation.current = document.activeElement;
    }, [pathname]);

    useEffect(() => {
        if (!title || focusedPath.current === pathname || !headingRef.current) return;
        focusedPath.current = pathname;
        const active = document.activeElement;
        const focusMoved =
            active !== null && active !== document.body && active !== focusAtNavigation.current;
        if (keepsNavigationOpen || focusMoved) return;
        headingRef.current.focus();
    }, [pathname, title, keepsNavigationOpen]);

    return headingRef;
}
